import * as usersActions from '@Actions/users';

import {
	BulkUpdateSingleSubscriptionUsersRequest,
	CreateUserResponse,
	User,
} from '@Types/user.types';
import {
	PaginatedUsers,
	UpdateRoleParams,
	UserParams,
} from '@Types/users.types';
import {
	RegisteredApplications,
	error as notificationError,
	success,
} from '@innovyze/stylovyze';
import {
	bulkUserOperationsService,
	changeRole,
	createUser,
	deleteUser,
	getSingleUserService,
	getUsers as getUsersService,
	updateUser,
} from '@Services/users';
import { put, retry, takeLatest } from 'redux-saga/effects';

import { AxiosError, AxiosResponse } from 'axios';
import { OperationStatus } from '@Types/operationStatus.types';
import { PayloadAction } from '@reduxjs/toolkit';
import { addRouteNamespace } from '@Utils/actions';
import { getUsers } from '@Actions/users';
import { navigateTo } from '@Actions/navigate.action';

function* getUsersSaga(action: PayloadAction<UserParams>) {
	const { getUsersResolved, getUsersRejected } = usersActions;
	try {
		const response: AxiosResponse<PaginatedUsers> = yield retry(
			5,
			1500,
			getUsersService,
			action.payload,
		);
		if (response.data) {
			yield put(getUsersResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getUsersRejected());
	}
}
function* getSingleUserSaga(action: PayloadAction<string>) {
	const { getSingleUserResolved, getSingleUserRejected } = usersActions;
	try {
		const response: AxiosResponse<User> = yield retry(
			5,
			1500,
			getSingleUserService,
			action.payload,
		);
		if (response.data) {
			yield put(getSingleUserResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getSingleUserRejected());
	}
}

function* deleteUserSaga(action: PayloadAction<string>) {
	const { deleteUserResolved, deleteUserRejected } = usersActions;
	try {
		const response: AxiosResponse<OperationStatus> = yield retry(
			5,
			1500,
			deleteUser,
			action.payload,
		);
		if (response.data.operationStatus === 'SUCCESS') {
			yield put(deleteUserResolved(true));
			yield put(getUsers({}));
			yield put(navigateTo(addRouteNamespace('user-management')));
			yield put(success('User was deleted successfully.'));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(deleteUserRejected());
		yield put(notificationError('User failed to be deleted.'));
	}
}

function* updateRoleSaga(action: PayloadAction<UpdateRoleParams>) {
	const { updateRoleResolved, updateRoleRejected } = usersActions;
	try {
		const response: AxiosResponse<OperationStatus> = yield retry(
			5,
			1500,
			changeRole,
			action.payload,
		);
		if (response.data.operationStatus === 'SUCCESS') {
			yield put(updateRoleResolved(true));
			yield put(getUsers({}));
			yield put(success('Changes were saved successfully.'));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(updateRoleRejected());
		yield put(notificationError('Changes failed to be saved'));
	}
}

function* updateUserSaga(action: PayloadAction<User>) {
	const { updateUserResolved, updateUserRejected } = usersActions;
	try {
		const response: AxiosResponse<OperationStatus> = yield retry(
			5,
			1500,
			updateUser,
			action.payload,
		);
		if (response.data.operationStatus === 'SUCCESS') {
			yield put(updateUserResolved());
			yield put(success('User updated successfully.'));
			yield put(navigateTo(addRouteNamespace('user-management')));
		} else {
			yield put(updateUserRejected());
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(updateUserRejected());
		yield put(notificationError('Changes failed to be saved'));
	}
}

function* bulkUpdateSingleSubscriptionUsersSaga(
	action: PayloadAction<BulkUpdateSingleSubscriptionUsersRequest>,
) {
	const { removeUserOperations, addUserOperations } = action.payload;
	const {
		bulkUpdateSingleSubscriptionUsersResolved,
		bulkUpdateSingleSubscriptionUsersRejected,
	} = usersActions;
	const BATCH_SIZE = 5;
	try {
		for (let i = 0; i < removeUserOperations.length; i = i + BATCH_SIZE) {
			yield retry(
				3,
				1500,
				bulkUserOperationsService,
				removeUserOperations.slice(i, i + BATCH_SIZE),
			);
		}
		for (let i = 0; i < addUserOperations.length; i = i + BATCH_SIZE) {
			yield retry(
				3,
				1500,
				bulkUserOperationsService,
				addUserOperations.slice(i, i + BATCH_SIZE),
			);
		}
		yield put(success('Users edited successfully'));
		yield put(navigateTo(addRouteNamespace('user-management')));
		yield put(bulkUpdateSingleSubscriptionUsersResolved());
	} catch (error) {
		yield put(notificationError('Failed to update users'));
		yield put(bulkUpdateSingleSubscriptionUsersRejected());
	}
}

function* createUserSaga(
	action: PayloadAction<{
		user: User;
		redirectTo?: string;
		applicationName?: RegisteredApplications;
	}>,
) {
	const { createUserResolved, createUserRejected } = usersActions;
	try {
		const response: AxiosResponse<CreateUserResponse> = yield retry(
			5,
			1500,
			createUser,
			action.payload.user,
			action.payload.applicationName,
		);
		if (response.data.created_at) {
			yield put(success('New user successfully created'));
			yield put(createUserResolved(true));
			if (action.payload.redirectTo) {
				yield put(navigateTo(action.payload.redirectTo));
			}
			yield put(getUsers({}));
		} else {
			throw new Error('created_at is blank');
		}
	} catch (ex) {
		const e = ex as AxiosError;
		console.error(e);
		yield put(createUserRejected());
		let errorMessage = 'Failed to create new user';
		if (e.response?.status == 409) {
			errorMessage = 'This email already exists';
		}
		if (e.response?.status == 404) {
			errorMessage = 'Invalid request data';
		}
		if (e.response?.status == 403) {
			errorMessage = 'Number of entitlements limit has been reached';
		}
		yield put(notificationError(errorMessage));
	}
}

function* watchGetUsers() {
	yield takeLatest(usersActions.getUsers, getUsersSaga);
}

function* watchGetSingleUsers() {
	yield takeLatest(usersActions.getSingleUser, getSingleUserSaga);
}

function* watchDeleteUser() {
	yield takeLatest(usersActions.deleteUser, deleteUserSaga);
}

function* watchUpdateRole() {
	yield takeLatest(usersActions.updateRole, updateRoleSaga);
}

function* watchUpdateUser() {
	yield takeLatest(usersActions.updateUser, updateUserSaga);
}

function* watchCreateUser() {
	yield takeLatest(usersActions.createUser, createUserSaga);
}

function* watchBulkUsers() {
	yield takeLatest(
		usersActions.bulkUpdateSingleSubscriptionUsers,
		bulkUpdateSingleSubscriptionUsersSaga,
	);
}

export default [
	watchGetUsers(),
	watchGetSingleUsers(),
	watchDeleteUser(),
	watchUpdateRole(),
	watchUpdateUser(),
	watchCreateUser(),
	watchBulkUsers(),
];
